import { gql, useQuery } from '@apollo/client';
import {
  CvlFinancialQualificationCriteriaEnumType,
  CvlPaymentStatusEnumType,
} from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';

import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import { AccreditationTab } from '../StepTypes';
import Documents from './Documents';
import FinancialDetails from './FinancialDetails';
import Payment from './Payment';
import PersonalDetails from './PersonalDetails';

const CVL_INVESTOR_APPLICATION_QUALIFICATION_QUERY = gql(`
  query InvestorAccreditationApplicationQualificationForDocuments($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      pincode
      financialDetails {
        qualificationCriteria
      }
      documents {
        id
      }
      paymentStatus
    }
  }
`);

type Props = {
  currentTab: AccreditationTab;
  onTabChange: (tab: AccreditationTab) => void;
  selectedInvestmentEntity: any | null;
  onProceedToSubmitStep: () => void;
  onBackStep: () => void;
  locked?: boolean;
};

const AccreditationApplication: FC<Props> = ({
  currentTab,
  onTabChange,
  selectedInvestmentEntity,
  onProceedToSubmitStep,
  onBackStep,
  locked,
}) => {
  const [qualificationCriteria, setQualificationCriteria] = useState<
    CvlFinancialQualificationCriteriaEnumType | ''
  >('');

  const { data: cvlData, refetch: refetchQualification } = useQuery(
    CVL_INVESTOR_APPLICATION_QUALIFICATION_QUERY,
    {
      variables: { investmentEntityId: selectedInvestmentEntity?.id },
      skip: !selectedInvestmentEntity?.id,
      fetchPolicy: 'network-only',
    }
  );

  const existingQualificationCriteria =
    (cvlData?.investorAccreditationApplication?.financialDetails?.qualificationCriteria as
      | CvlFinancialQualificationCriteriaEnumType
      | undefined) ?? '';

  const paymentStatus = (cvlData?.investorAccreditationApplication?.paymentStatus ?? null) as
    | CvlPaymentStatusEnumType
    | null
    | undefined;

  const isLockedAfterPayment = paymentStatus === CvlPaymentStatusEnumType.Success;

  const hasPersonalDetails = !!cvlData?.investorAccreditationApplication?.pincode;
  const hasFinancialDetails = !!cvlData?.investorAccreditationApplication?.financialDetails;
  const hasDocuments = !!cvlData?.investorAccreditationApplication?.documents?.length;
  const canAccessPayment =
    isLockedAfterPayment || (hasPersonalDetails && hasFinancialDetails && hasDocuments);

  useEffect(() => {
    if (existingQualificationCriteria && !qualificationCriteria) {
      setQualificationCriteria(existingQualificationCriteria);
    }
  }, [existingQualificationCriteria, qualificationCriteria]);

  useEffect(() => {
    if (isLockedAfterPayment && currentTab !== 'payment') {
      onTabChange('payment');
    }
  }, [isLockedAfterPayment, currentTab, onTabChange]);

  const {
    showToast: showSavedToast,
    handleToastOpen: handleSavedToastOpen,
    handleToastClose: handleSavedToastClose,
  } = useToast();

  const tabs: { id: AccreditationTab; label: string; disabled?: boolean }[] = [
    { id: 'personal', label: 'Personal Details' },
    { id: 'financial', label: 'Financial Details' },
    { id: 'documents', label: 'Documents' },
    { id: 'payment', label: 'Payment' },
  ];

  return (
    <div className="mt-6">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Accreditation application tabs">
          {tabs.map(tab => {
            const isActive = currentTab === tab.id;
            const isLockedTab =
              locked ||
              (isLockedAfterPayment &&
                (tab.id === 'personal' || tab.id === 'financial' || tab.id === 'documents')) ||
              (tab.id === 'payment' && !canAccessPayment);

            return (
              <button
                key={tab.id}
                type="button"
                disabled={isLockedTab}
                title={
                  tab.id === 'payment' && !canAccessPayment
                    ? 'Complete personal details, financial details and documents first'
                    : undefined
                }
                className={[
                  'whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium focus:outline-none',
                  isLockedTab
                    ? 'cursor-not-allowed border-transparent text-gray-300'
                    : isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                ].join(' ')}
                onClick={() => {
                  if (!isLockedTab) onTabChange(tab.id);
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="mt-6 rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-700">
        {currentTab === 'personal' && (
          <PersonalDetails
            selectedInvestmentEntity={selectedInvestmentEntity}
            locked={locked || isLockedAfterPayment}
            onSuccess={() => {
              refetchQualification();
              handleSavedToastOpen();
              onTabChange('financial');
            }}
          />
        )}

        {currentTab === 'financial' && (
          <FinancialDetails
            selectedInvestmentEntity={selectedInvestmentEntity}
            locked={locked || isLockedAfterPayment}
            onSuccess={data => {
              setQualificationCriteria(data.qualificationCriteria);
              refetchQualification();
              handleSavedToastOpen();
              onTabChange('documents');
            }}
            onBack={() => onTabChange('personal')}
          />
        )}

        {currentTab === 'documents' && (
          <Documents
            qualificationCriteria={qualificationCriteria}
            selectedInvestmentEntity={selectedInvestmentEntity}
            locked={locked || isLockedAfterPayment}
            onSuccess={() => {
              refetchQualification();
              handleSavedToastOpen();
              onTabChange('payment');
            }}
            onBack={() => onTabChange('financial')}
          />
        )}

        {currentTab === 'payment' && (
          <Payment
            selectedInvestmentEntity={selectedInvestmentEntity}
            onProceedToSubmitStep={onProceedToSubmitStep}
            onBack={() => onTabChange('documents')}
          />
        )}
      </div>
      <Toast
        type="success"
        title="Details saved"
        message="Your details have been saved successfully."
        show={showSavedToast}
        handleClose={handleSavedToastClose}
      />
    </div>
  );
};

export default AccreditationApplication;
